import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { onCLS, onINP, onFCP, onLCP, onTTFB } from 'web-vitals'
import * as Sentry from '@sentry/react'
import { registerSW } from 'virtual:pwa-register'
import './index.css'
import App from './App.jsx'
import { PRELOAD_RELOAD_KEY } from './utils/lazyWithReload'

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN || undefined,
  environment: import.meta.env.MODE,
  release: import.meta.env.VITE_APP_VERSION,
  // Nur in Production aktiv — lokal werden keine Events gesendet
  enabled: import.meta.env.PROD && !!import.meta.env.VITE_SENTRY_DSN,
  tracesSampleRate: 0.1,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({ maskAllText: true, blockAllMedia: true }),
  ],
  replaysSessionSampleRate: 0,
  replaysOnErrorSampleRate: 1.0,
})

function reportWebVital({ name, value, rating }) {
  window.umami?.track('web-vital', { metric: name, value: Math.round(value), rating })
}
onCLS(reportWebVital)
onINP(reportWebVital)
onFCP(reportWebVital)
onLCP(reportWebVital)
onTTFB(reportWebVital)

// PWA install prompt vor React-Render abfangen (Race Condition fix)
window.__pwaPrompt = null
window.addEventListener('beforeinstallprompt', (e) => {
  e.preventDefault()
  window.__pwaPrompt = e
})

// Unhandled Promise Rejections an Sentry melden (z.B. Firebase-Fehler die nicht
// per try/catch abgefangen wurden)
window.addEventListener('unhandledrejection', (event) => {
  Sentry.captureException(event.reason)
})

// Als SPA lösen wir nach dem ersten Laden keine Navigation mehr aus — der Browser
// würde sw.js also nie erneut auf Updates prüfen. Deshalb aktiv pollen, damit ein
// Deploy auch bei dauerhaft geöffnetem Tab ankommt.
const SW_UPDATE_INTERVAL = 30 * 60 * 1000
let lastSWUpdateCheck = 0

registerSW({
  immediate: true,
  onRegisteredSW(_swUrl, registration) {
    if (!registration) return

    const checkForUpdate = () => {
      const now = Date.now()
      if (now - lastSWUpdateCheck < SW_UPDATE_INTERVAL) return
      lastSWUpdateCheck = now
      registration.update().catch(() => {})
    }

    setInterval(checkForUpdate, SW_UPDATE_INTERVAL)
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') checkForUpdate()
    })
  },
  onRegisterError(err) {
    // TWA-Crawler (PlayStore-Google) und In-App-Browser lehnen SW-Registrierung ab.
    console.warn('[PWA] SW registration failed:', err?.message)
  },
})

// Stale chunk nach Deploy: Seite einmal neu laden, damit das frische
// index.html und die aktuellen Chunk-Hashes geholt werden.
window.addEventListener('vite:preloadError', (event) => {
  if (sessionStorage.getItem(PRELOAD_RELOAD_KEY)) return
  sessionStorage.setItem(PRELOAD_RELOAD_KEY, '1')
  event.preventDefault()
  window.location.reload()
})
window.addEventListener('load', () => {
  sessionStorage.removeItem(PRELOAD_RELOAD_KEY)
})

createRoot(document.getElementById('root')).render(
  <StrictMode>
    <App />
  </StrictMode>,
)
